import React, { useState, useEffect } from 'react';
import Icon from '../UI/Icon';
import { TWO_FACTOR_CONFIG } from '../../constants/security';
import { totpService } from '../../services/totpService';
import { useAuth } from '../../contexts/AuthContext';
import './TwoFactorAuth.css';

/**
 * 2단계 인증(2FA) 관리 컴포넌트
 * TOTP(Time-based One-Time Password) 지원
 */
function TwoFactorAuth({ isOpen, onClose, onSetupComplete }) {
  console.log('TwoFactorAuth 렌더링됨:', { isOpen });
  
  const { user } = useAuth();
  const [step, setStep] = useState('intro'); // intro, setup, verify, complete
  const [secretKey, setSecretKey] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('TwoFactorAuth mounted/updated:', { isOpen, user: user?.email });
  }, [isOpen, user]);

  // 2FA 설정 시작
  const handleSetupStart = async () => {
    setLoading(true);
    setError('');
    
    try {
      // TOTP 시크릿 생성
      const secret = totpService.generateSecret();
      const keyUri = totpService.generateKeyUri(user.email, secret);
      const qrCode = await totpService.generateQRCode(keyUri);
      
      setSecretKey(secret);
      setQrCodeUrl(qrCode);
      setStep('setup');
    } catch (err) {
      console.error('2FA 설정 실패:', err);
      setError('2FA 설정을 시작할 수 없습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // 인증 코드 검증
  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('6자리 인증 코드를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // TOTP 코드 검증
      const isValid = totpService.verify(verificationCode, secretKey);
      
      if (isValid) {
        // 백업 코드 생성
        const codes = totpService.generateBackupCodes();
        setBackupCodes(codes);
        
        // 2FA 설정 저장
        await totpService.saveTwoFactorSettings(user.uid, {
          secret: secretKey,
          backupCodes: codes,
          enabled: true
        });
        
        setStep('complete');
        
        if (onSetupComplete) {
          onSetupComplete({
            secretKey,
            backupCodes: codes,
            enabled: true
          });
        }
      } else {
        setError('인증 코드가 올바르지 않습니다. 다시 시도해주세요.');
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('2FA 검증 오류:', err.message);
      }
      setError('인증 코드 검증 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 백업 코드 다운로드
  const handleDownloadBackupCodes = () => {
    const content = [
      '# NewPass2 백업 코드',
      '# 안전한 곳에 보관하세요',
      '# 각 코드는 한 번만 사용할 수 있습니다',
      '',
      ...backupCodes.map((code, index) => `${index + 1}. ${code}`)
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'newpass2-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="two-factor-modal-overlay" style={{ zIndex: 9999 }}>
      <div className="two-factor-modal" style={{ backgroundColor: 'white', zIndex: 10000 }}>
        <div className="two-factor-header">
          <h2>2단계 인증 설정</h2>
          <button className="close-button" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>

        <div className="two-factor-content">
          {/* 소개 단계 */}
          {step === 'intro' && (
            <div className="step-intro">
              <div className="step-icon">🛡️</div>
              <h3>계정 보안을 강화하세요</h3>
              <p>
                2단계 인증을 활성화하면 로그인 시 패스워드와 함께 
                인증 앱에서 생성된 6자리 코드가 필요합니다.
              </p>
              
              <div className="benefits-list">
                <div className="benefit-item">
                  <Icon name="check" />
                  <span>무단 접근 방지</span>
                </div>
                <div className="benefit-item">
                  <Icon name="check" />
                  <span>계정 보안 강화</span>
                </div>
                <div className="benefit-item">
                  <Icon name="check" />
                  <span>산업 표준 보안</span>
                </div>
              </div>

              <div className="supported-apps">
                <h4>지원하는 인증 앱:</h4>
                <ul>
                  <li>Google Authenticator</li>
                  <li>Microsoft Authenticator</li>
                  <li>Authy</li>
                  <li>1Password</li>
                </ul>
              </div>

              <button 
                className="setup-button primary"
                onClick={handleSetupStart}
                disabled={loading}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  marginTop: '24px',
                  display: 'block',
                  margin: '24px auto 0'
                }}
              >
                {loading ? '설정 중...' : '2FA 설정 시작'}
              </button>
            </div>
          )}

          {/* 설정 단계 */}
          {step === 'setup' && (
            <div className="step-setup">
              <h3>인증 앱에 계정 추가</h3>
              
              <div className="setup-instructions">
                <div className="instruction-step">
                  <span className="step-number">1</span>
                  <p>인증 앱에서 새 계정을 추가하세요</p>
                </div>
                <div className="instruction-step">
                  <span className="step-number">2</span>
                  <p>아래 QR 코드를 스캔하거나 수동으로 키를 입력하세요</p>
                </div>
              </div>

              <div className="qr-code-section">
                <div className="qr-code-container">
                  <img src={qrCodeUrl} alt="2FA QR Code" className="qr-code" />
                </div>
                
                <div className="manual-key">
                  <p>수동 입력용 키:</p>
                  <div className="secret-key">
                    <code>{secretKey}</code>
                    <button 
                      className="copy-key-button"
                      onClick={() => navigator.clipboard.writeText(secretKey)}
                    >
                      <Icon name="copy" />
                    </button>
                  </div>
                </div>
              </div>

              <button 
                className="next-button primary"
                onClick={() => setStep('verify')}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '24px',
                  display: 'block',
                  margin: '24px auto 0'
                }}
              >
                다음 단계
              </button>
            </div>
          )}

          {/* 검증 단계 */}
          {step === 'verify' && (
            <div className="step-verify">
              <h3>인증 코드 확인</h3>
              <p>인증 앱에 표시된 6자리 코드를 입력하세요:</p>

              <div className="verification-input">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                    setError('');
                  }}
                  placeholder="123456"
                  className="code-input"
                  maxLength="6"
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="verify-actions">
                <button 
                  className="back-button secondary"
                  onClick={() => setStep('setup')}
                >
                  이전
                </button>
                <button 
                  className="verify-button primary"
                  onClick={handleVerify}
                  disabled={loading || verificationCode.length !== 6}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (loading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer',
                    opacity: (loading || verificationCode.length !== 6) ? 0.7 : 1
                  }}
                >
                  {loading ? '확인 중...' : '확인'}
                </button>
              </div>
            </div>
          )}

          {/* 완료 단계 */}
          {step === 'complete' && (
            <div className="step-complete">
              <div className="success-icon">✅</div>
              <h3>2단계 인증이 활성화되었습니다!</h3>
              
              <div className="backup-codes-section">
                <h4>백업 코드</h4>
                <p>인증 앱에 접근할 수 없을 때 사용할 수 있는 일회용 백업 코드입니다:</p>
                
                <div className="backup-codes">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="backup-code">
                      {code}
                    </div>
                  ))}
                </div>

                <div className="backup-warning">
                  ⚠️ 이 코드들을 안전한 곳에 저장하세요. 각 코드는 한 번만 사용할 수 있습니다.
                </div>

                <button 
                  className="download-button secondary"
                  onClick={handleDownloadBackupCodes}
                >
                  <Icon name="download" />
                  백업 코드 다운로드
                </button>
              </div>

              <button 
                className="complete-button primary"
                onClick={onClose}
              >
                완료
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TwoFactorAuth;